import { Archive, ArchiveRestore, ChevronRight, ExternalLink } from 'lucide-react';
import { AppRecordHistory } from '@/components/apps/AppRecordHistory';
import { AppRecordBar } from '@/components/apps/AppRecordBar';
import { AppRecordFields } from '@/components/apps/AppRecordFields';
import { AppLabelsField } from '@/components/apps/AppLabelsField';
import { AppFolderPicker } from '@/components/apps/AppFolderPicker';
import { AppRecordComments } from '@/components/apps/AppRecordComments';
import type { AppLabel } from '@/lib/app-collaboration';
import type { DataTableCellTone } from '@shared/types/views';
import type { ReactNode } from 'react';
import type { AppCollection, AppMember, AppRecord, AppRecordChanges } from '@/lib/app-records';
import { listColumns } from './app-records-model';

export interface AppRecordCollaborationProps {
  appId: string;
  labels: AppLabel[];
  selectedLabels: string[];
  canCreateLabel: boolean;
  teams: Array<{ id: string; name: string }>;
  onToggleLabel: (labelId: string, on: boolean) => Promise<void>;
  onCreateLabel: (name: string, tone: DataTableCellTone) => Promise<void>;
  onCommented: () => void;
  historyKey: number;
}

interface AppRecordPanelProps {
  collaboration: AppRecordCollaborationProps;
  /** F5c: the review with the app's grid, when it has one. */
  review?: ReactNode;
  /** What to do next with the object, right under its bar (N1). */
  next?: ReactNode;
  /** S1: the tools proposed on the object, in its bar. */
  tools?: ReactNode;
  /** Shown between the bar and the fields (e.g. the run's result of a « one result per run » app). */
  lead?: ReactNode;
  /** The CRM account of the object, after its fields; renders nothing when there is none. */
  account?: ReactNode;
  /** « Rattacher à… » another object of the list (R4), for whoever can archive. */
  attach?: ReactNode;
  record: AppRecord;
  collection: AppCollection;
  /** The columns the app's list shows (its description's choice); they come first in the object. */
  listColumnKeys?: string[];
  members: AppMember[];
  canManage: boolean;
  onChange: (changes: AppRecordChanges) => Promise<void>;
  onArchive: (archived: boolean) => Promise<void>;
  onUndo: (eventId: string) => Promise<void>;
  onOpenRun?: (runId: string) => void;
}

const menuItem = 'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-domino-ink hover:bg-domino-muted';

/**
 * One object of the app: what Domino found comes first. Above it, one line
 * with what people set; every other control waits in « … ». Nothing empty
 * takes room (docs/CONVENTIONS.md, « Rien de vide ne prend de place »).
 */
export const AppRecordPanel = ({ collaboration, review, next, tools, lead, account, attach, record, collection, listColumnKeys, members, canManage, onChange, onArchive, onUndo, onOpenRun }: AppRecordPanelProps) => {
  const runAction = record.lastRunId && onOpenRun;
  const menu = <>
    <label className="block text-xs text-domino-inkSoft">
      Échéance
      <input type="date" value={record.dueDate ?? ''} onChange={(event) => void onChange({ dueDate: event.target.value || null })}
        className="mt-1 block w-full rounded-lg border border-domino-cardBorder bg-domino-white px-2 py-1 text-sm text-domino-ink" />
    </label>
    <AppLabelsField labels={collaboration.labels} selected={collaboration.selectedLabels} canCreate={collaboration.canCreateLabel}
      onToggle={collaboration.onToggleLabel} onCreate={collaboration.onCreateLabel} />
    <AppFolderPicker recordId={record.id} teams={collaboration.teams} />
    {(runAction || canManage) && (
      <div className="-mx-2 border-t border-domino-cardBorder pt-2">
        {runAction && (
          <button type="button" role="menuitem" className={menuItem} onClick={() => onOpenRun(record.lastRunId!)}>
            <ExternalLink className="h-4 w-4 text-domino-inkSoft" />{collection.mode === 'runs' ? 'Ouvrir le résultat' : 'Voir la dernière exécution'}
          </button>
        )}
        {canManage && (
          <button type="button" role="menuitem" className={menuItem} onClick={() => void onArchive(!record.archivedAt)}>
            {record.archivedAt ? <ArchiveRestore className="h-4 w-4 text-domino-inkSoft" /> : <Archive className="h-4 w-4 text-domino-inkSoft" />}
            {record.archivedAt ? 'Sortir des archives' : 'Archiver'}
          </button>
        )}
        {canManage && attach}
      </div>
    )}
  </>;

  return (
    <section aria-label="Fiche" className="space-y-5">
      <AppRecordBar record={record} collection={collection} members={members} onChange={onChange} menu={menu} tools={tools}
        labels={collaboration.labels.filter((label) => collaboration.selectedLabels.includes(label.id))} />

      {next}

      {lead}

      <AppRecordFields record={record} collection={collection} deciding={listColumns(collection, listColumnKeys).map((column) => column.key)} />

      {account}

      {review && (
        <div>
          <h4 className="text-sm font-semibold text-domino-ink">Revue</h4>
          <div className="mt-2">{review}</div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold text-domino-ink">Commentaires</h4>
        <div className="mt-2">
          <AppRecordComments appId={collaboration.appId} recordId={record.id} members={members} teams={collaboration.teams} onPosted={collaboration.onCommented} />
        </div>
      </div>

      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-1.5 text-sm font-semibold text-domino-ink">
          <ChevronRight className="h-4 w-4 text-domino-inkSoft transition group-open:rotate-90" aria-hidden="true" />Historique
        </summary>
        <div className="mt-2"><AppRecordHistory record={record} collection={collection} members={members} refreshKey={collaboration.historyKey} onUndo={onUndo} /></div>
      </details>
    </section>
  );
};
